import { useState, useRef } from 'react';
import type { NotificationConfig, Channel } from '../types';

interface SettingsModalProps {
  notification: NotificationConfig;
  onClose: () => void;
  onSave: (notification: NotificationConfig) => void;
}

export default function SettingsModal({ notification, onClose, onSave }: SettingsModalProps) {
  const [formData, setFormData] = useState<NotificationConfig>(notification);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChannelChange = (channel: Channel) => {
    const newChannels = formData.channels.includes(channel)
      ? formData.channels.filter(c => c !== channel)
      : [...formData.channels, channel];
    setFormData({ ...formData, channels: newChannels });
  };

  const handleSmsTemplateChange = (value: string) => {
    setFormData({ ...formData, smsTemplateId: value });
  };

  const handleInsiteTemplateChange = (value: string) => {
    setFormData({ ...formData, insiteTemplate: value });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({
        ...formData,
        emailTemplate: {
          name: file.name,
          url: URL.createObjectURL(file)
        }
      });
    }
  };

  const handleDeleteTemplate = () => {
    setFormData({ ...formData, emailTemplate: undefined });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = () => {
    onSave(formData);
  };

  const needsCustomValue = ['cloud-pc-expiry', 'cloud-pc-release-warning', 'balance-low', 'points-low', 'storage-low', 'inactive-user'].includes(notification.id);
  const isVerificationCode = notification.id === 'verification-code';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">通知设置</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">名称</label>
            <input
              type="text"
              value={formData.name}
              disabled
              className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">发送规则</label>
            <div className="flex items-start gap-3">
              <div className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 text-sm">
                {formData.rule}
              </div>
              {needsCustomValue && (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={formData.customValue || 0}
                    onChange={(e) => setFormData({ ...formData, customValue: parseInt(e.target.value) || 0 })}
                    className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    min="0"
                  />
                  <span className="text-sm text-gray-500">
                    {notification.id === 'storage-low' ? '%' : notification.id.includes('balance') || notification.id.includes('points') ? '元' : '天'}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">发送渠道</label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.channels.includes('sms')}
                  onChange={() => handleChannelChange('sms')}
                  className="w-4 h-4 text-teal-500 border-gray-300 rounded cursor-pointer"
                />
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-message-3-line text-teal-600"></i>
                  </div>
                  <span className="text-sm text-gray-700">短信发送</span>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.channels.includes('email')}
                  onChange={() => handleChannelChange('email')}
                  className="w-4 h-4 text-teal-500 border-gray-300 rounded cursor-pointer"
                />
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-line text-teal-600"></i>
                  </div>
                  <span className="text-sm text-gray-700">邮箱发送</span>
                </div>
              </label>
              {!isVerificationCode && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.channels.includes('insite')}
                    onChange={() => handleChannelChange('insite')}
                    className="w-4 h-4 text-teal-500 border-gray-300 rounded cursor-pointer"
                  />
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-notification-3-line text-teal-600"></i>
                    </div>
                    <span className="text-sm text-gray-700">站内信发送</span>
                  </div>
                </label>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">发送模板</label>
            <div className="space-y-4">
              {formData.channels.includes('sms') && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-message-3-line text-teal-600 text-sm"></i>
                    </div>
                    <span className="text-sm text-gray-700">短信模板 ID</span>
                  </div>
                  <input
                    type="text"
                    value={formData.smsTemplateId || ''}
                    onChange={(e) => handleSmsTemplateChange(e.target.value)}
                    placeholder="请输入短信模板 ID"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
              )}

              {formData.channels.includes('email') && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-line text-teal-600 text-sm"></i>
                    </div>
                    <span className="text-sm text-gray-700">邮箱模板</span>
                  </div>
                  {formData.emailTemplate ? (
                    <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded">
                        <i className="ri-file-text-line text-teal-600"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-900 truncate">{formData.emailTemplate.name}</div>
                      </div>
                      <button
                        onClick={handleDeleteTemplate}
                        className="w-8 h-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded cursor-pointer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  ) : (
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".html,.htm"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-teal-500 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-upload-cloud-line mr-2"></i>
                        上传邮箱模板
                      </button>
                    </div>
                  )}
                </div>
              )}

              {formData.channels.includes('insite') && !isVerificationCode && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-notification-3-line text-teal-600 text-sm"></i>
                    </div>
                    <span className="text-sm text-gray-700">站内信模板</span>
                  </div>
                  <textarea
                    value={formData.insiteTemplate || ''}
                    onChange={(e) => handleInsiteTemplateChange(e.target.value)}
                    placeholder="请输入站内信模板内容，支持变量：{orderNo}、{amount}、{cloudPcName}、{days}、{balance}、{points}、{percent}、{freeSpace} 等"
                    rows={4}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm resize-none"
                  />
                  <div className="mt-2 text-xs text-gray-500">
                    提示：使用 {'{变量名}'} 格式插入动态内容，例如：{'{orderNo}'}、{'{amount}'}、{'{cloudPcName}'} 等
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">状态</label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  checked={formData.enabled}
                  onChange={() => setFormData({ ...formData, enabled: true })}
                  className="w-4 h-4 text-teal-500 border-gray-300 cursor-pointer"
                />
                <span className="text-sm text-gray-700">启用</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  checked={!formData.enabled}
                  onChange={() => setFormData({ ...formData, enabled: false })}
                  className="w-4 h-4 text-teal-500 border-gray-300 cursor-pointer"
                />
                <span className="text-sm text-gray-700">禁用</span>
              </label>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2 bg-teal-500 text-white text-sm rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
}
